import React, { useEffect, useState } from 'react';
import {
  Users,
  FolderOpen,
  Clock,
  FileText,
  CreditCard,
  BarChart3,
  Calculator,
  Plus,
  Filter
} from 'lucide-react';
import CollaboratorsList from './collaborators/CollaboratorsList';
import ProjectsList from './collaborators/ProjectsList';
import TimesheetsList from './collaborators/TimesheetsList';
import InvoicesList from './collaborators/InvoicesList';
import PaymentsList from './collaborators/PaymentsList';
import CollaboratorsReports from './collaborators/CollaboratorsReports';
import AccountingTab from './collaborators/AccountingTab';
import { usePermissions } from '../hooks/usePermissions';
import { supabase } from '../services/supabaseService';

const CollaboratorsModule: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'collaborators' | 'projects' | 'timesheets' | 'invoices' | 'payments' | 'reports' | 'accounting'>('collaborators');
  const [initialProjectId, setInitialProjectId] = useState<string | null>(null);

  const { isSuperAdmin: isAdmin, isCollaboradorProgramador } = usePermissions();
  const isProgrammerView = isCollaboradorProgramador();

  // Navegación cruzada:
  //  - "navigate-to-project" viene del dashboard del colaborador (abrir un
  //    proyecto asignado desde la card).
  //  - "navigate-to-timesheet" viene del NotificationCenter (notif de nuevo
  //    parte de horas). Además del cambio de tab, la sub-tab salta a
  //    "timesheets" y se puede pre-cargar un parte concreto.
  const [initialTimesheetId, setInitialTimesheetId] = useState<string | null>(null);

  useEffect(() => {
    const projectHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      if (detail.projectId) {
        setInitialProjectId(detail.projectId);
        setActiveTab('projects');
      }
    };
    const timesheetHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      setActiveTab('timesheets');
      if (detail.timesheetId) setInitialTimesheetId(detail.timesheetId);
    };
    const subTabHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      if (detail.subTab === 'invoices') setActiveTab('invoices');
      else if (detail.subTab === 'payments') setActiveTab('payments');
      else if (detail.subTab === 'timesheets') setActiveTab('timesheets');
    };
    window.addEventListener('navigate-to-project', projectHandler);
    window.addEventListener('navigate-to-timesheet', timesheetHandler);
    window.addEventListener('navigate-to-payments-tab', subTabHandler);
    return () => {
      window.removeEventListener('navigate-to-project', projectHandler);
      window.removeEventListener('navigate-to-timesheet', timesheetHandler);
      window.removeEventListener('navigate-to-payments-tab', subTabHandler);
    };
  }, []);

  // Badge de partes pendientes de aprobar (solo admin).
  const [pendingTimesheets, setPendingTimesheets] = useState(0);
  useEffect(() => {
    if (isProgrammerView) return;
    (async () => {
      const { count } = await supabase
        .from('timesheets')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'submitted');
      setPendingTimesheets(count || 0);
    })();
  }, [activeTab, isProgrammerView]);

  const allTabs = [
    { id: 'collaborators', label: isProgrammerView ? 'Mi Información' : 'Plantilla', icon: Users },
    { id: 'projects', label: isProgrammerView ? 'Mis Proyectos' : 'Proyectos', icon: FolderOpen },
    { id: 'timesheets', label: isProgrammerView ? 'Mis Horas' : 'Partes de Horas', icon: Clock, badge: !isProgrammerView ? pendingTimesheets : 0 },
    { id: 'invoices', label: isProgrammerView ? 'Mis Facturas' : 'Facturas', icon: FileText },
    { id: 'payments', label: isProgrammerView ? 'Mis Pagos' : 'Pagos', icon: CreditCard },
    { id: 'accounting', label: 'Contabilidad', icon: Calculator, adminOnly: true },
    { id: 'reports', label: 'Reportes', icon: BarChart3, adminOnly: true }
  ];

  const tabs = allTabs.filter(tab => !tab.adminOnly || isAdmin());

  const renderContent = () => {
    switch (activeTab) {
      case 'collaborators':
        return <CollaboratorsList />;
      case 'projects':
        return <ProjectsList initialProjectId={initialProjectId} onProjectConsumed={() => setInitialProjectId(null)} />;
      case 'timesheets':
        return <TimesheetsList initialTimesheetId={initialTimesheetId} onTimesheetConsumed={() => setInitialTimesheetId(null)} />;
      case 'invoices':
        return <InvoicesList />;
      case 'payments':
        return <PaymentsList />;
      case 'reports':
        return <CollaboratorsReports />;
      case 'accounting':
        return <AccountingTab />;
      default:
        return <CollaboratorsList />;
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isProgrammerView ? 'Mi Panel de Colaborador' : 'Gestión de Plantilla'}
        </h1>
        <p className="text-gray-600">
          {isProgrammerView
            ? 'Gestiona tu información, proyectos, horas y pagos'
            : 'Gestión completa de colaboradores, proyectos, horas y pagos'
          }
        </p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                  {(tab as any).badge > 0 && (
                    <span className="ml-1 text-xs bg-red-500 text-white rounded-full px-2 py-0.5 font-semibold">
                      {(tab as any).badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default CollaboratorsModule;